import React, { useCallback, useEffect, useState } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';
import { HiddenPopoverState, PopoverProps } from './types';

export function usePopover({
  color,
  defaultOpen,
  isModal,
  isOpen: propsIsOpen,
  maxWidth,
  minWidth,
  offset: propsOffset,
  onOpen,
  placement,
  role: propsRole,
  triggerClassName,
  width,
  setHideInsteadOfClose,
  hideInsteadOfClose,
}: Omit<PopoverProps, 'children'> & HiddenPopoverState) {
  const [isOpen, setIsOpen] = useState(propsIsOpen ?? defaultOpen);

  useEffect(() => {
    setIsOpen(propsIsOpen ?? defaultOpen);
  }, [propsIsOpen, defaultOpen]);

  const handleOpen = useCallback(
    (collapsed: boolean) => {
      if (onOpen) {
        onOpen(collapsed);
      }
      if (propsIsOpen === undefined || propsIsOpen === null) {
        setIsOpen(collapsed);
      }
    },
    [onOpen]
  );

  const middleware = [
    offset(propsOffset ?? 2),
    flip({
      fallbackAxisSideDirection: 'end',
    }),
    shift({ padding: 2 }),
  ];

  middleware.push(
    size({
      apply({ availableHeight, availableWidth, rects, elements }) {
        Object.assign(elements.floating.style, {
          maxHeight: `${availableHeight}px`,
          ...(minWidth !== undefined && {
            minWidth: `${
              minWidth === 'available-width'
                ? availableWidth
                : minWidth === 'trigger-width'
                ? rects.reference.width
                : minWidth
            }px`,
          }),
          ...(width !== undefined && {
            width: `${
              width === 'available-width' ? availableWidth : width === 'trigger-width' ? rects.reference.width : width
            }px`,
          }),
          ...(maxWidth !== undefined && {
            maxWidth: `${
              maxWidth === 'available-width'
                ? availableWidth
                : maxWidth === 'trigger-width'
                ? rects.reference.width
                : maxWidth
            }px`,
          }),
        });
      },
    })
  );

  const data = useFloating({
    placement,
    open: isOpen,
    onOpenChange: handleOpen,
    whileElementsMounted: autoUpdate,
    middleware,
  });

  const context = data.context;

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: propsRole });

  const interactions = useInteractions([dismiss, role]);

  return React.useMemo(
    () => ({
      color,
      isOpen,
      handleOpen,
      ...interactions,
      ...data,
      isModal,
      triggerClassName,
      setHideInsteadOfClose,
      hideInsteadOfClose,
    }),
    [
      color,
      isOpen,
      handleOpen,
      interactions,
      data,
      isModal,
      triggerClassName,
      setHideInsteadOfClose,
      hideInsteadOfClose,
    ]
  );
}
